import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useRugianoProjectApi from "hooks/api/private/useRugianoProjectApi";
import useSnackbar from "hooks/useSnackbar";

import { RugianoProjectListItem } from "types/api/rugianoProject";

export type RugianoProjectListHook = {
    navigateToNewProject: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<RugianoProjectListItem>;
    alertConfirmHook: AlertConfirmHookType;
};

function useRugianoProjectsList(): RugianoProjectListHook {
    const { getAllRugianoProject, deleteRugianoProject } = useRugianoProjectApi();

    const navigate = useNavigate();

    const navigateToEditProject = React.useCallback(
        (row: RugianoProjectListItem) => {
            navigate(makeUrl(":projectId/modifica", { projectId: row.id }));
        },
        [navigate],
    );

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    type: {
                        key: "type",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "rugiano-project-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;
                newForm.search.type = value;
                newForm.search.slug = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina progetto",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteRugianoProjectButton = React.useCallback(
        (row: RugianoProjectListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare il progetto?", () => {
                const [apiCall] = deleteRugianoProject({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Progetto eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del progetto"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteRugianoProject,
        ],
    );

    const columns: Array<Column<RugianoProjectListItem>> = React.useMemo(
        () => [
            {
                id: "projects.graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: RugianoProjectListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "projects.name",
                header: "Nome",
                render: (row: RugianoProjectListItem) => row.name,
                orderable: true,
            },
            {
                id: "projects.type",
                header: "Tipologia",
                render: (row: RugianoProjectListItem) => row.type,
                orderable: true,
            },
            {
                id: "projects.slug",
                header: "Slug",
                render: (row: RugianoProjectListItem) => row.slug,
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: RugianoProjectListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditProject}
                        onDelete={deleteRugianoProjectButton}
                    />
                ),
            },
        ],
        [navigateToEditProject, deleteRugianoProjectButton],
    );

    const dataTableHook = useDataTableWithMemory<RugianoProjectListItem>({
        apiCall: getAllRugianoProject,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "rugiano-project-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewProject = React.useCallback(() => {
        navigate(makeUrl("nuovo"));
    }, [navigate]);

    return React.useMemo(
        (): RugianoProjectListHook => ({
            navigateToNewProject,
            onSearchFilterChange,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        }),
        [
            navigateToNewProject,
            onSearchFilterChange,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        ],
    );
}

export default useRugianoProjectsList;
